/**
 * Academy domain — primitive aliases, enum unions, and numeric↔name lookup tables.
 * Source of truth: `docs/academy-api.types.ts` / `docs/academy-api-integration.md` §6.1.
 *
 * The deployed API has NO string-enum JSON converter, so enums travel as NUMERIC
 * codes in BOTH directions (confirmed live 2026-06-10 — `OPTIONS /LiveEvents`
 * returns `LiveEventStatusType: 1`, etc.):
 * - WRITE bodies must send codes — names are rejected ("The JSON value could not
 *   be converted to ...Enums.OwnerType") — see `toWireEnums`;
 * - READ responses arrive with codes and are normalized back to the NAMES the
 *   TS unions declare at every read boundary — see `fromWireEnums`.
 * The numeric→name tables also coerce SignalR hub payloads (integration guide §9).
 */

// ----------------------------------------------------------------------------
// Primitive aliases
// ----------------------------------------------------------------------------

/** GUID serialized as string (uuid). */
export type Guid = string;

/** DateTime ISO-8601 in UTC, e.g. "2026-06-08T18:00:00Z". */
export type DateTimeUtc = string;

/** 64-bit (`long`) on the server; `number` in JS (counters stay within safe range). */
export type Long = number;

// ----------------------------------------------------------------------------
// Enum unions (travel by NAME over REST)
// ----------------------------------------------------------------------------

/** Platform=1, Tenant=2. MVP accepts only Platform. */
export type OwnerType = 'Platform' | 'Tenant';

/** FacilFit=1, Actuar=2, Ediz=3, Toletus=4, Tickou=5. Default 0 (unlabeled) has no member. */
export type ProductType = 'FacilFit' | 'Actuar' | 'Ediz' | 'Toletus' | 'Tickou';

/**
 * User-selectable products.
 */
export const ACADEMY_PRODUCT_OPTIONS = [
  'FacilFit',
  'Actuar',
  'Ediz',
  'Toletus',
  'Tickou',
] as const satisfies readonly ProductType[];

/** Scheduled=1, Live=2, Ended=3, Recorded=4, Cancelled=5 (webhook-driven, monotonic). */
export type LiveEventStatusType =
  | 'Scheduled'
  | 'Live'
  | 'Ended'
  | 'Recorded'
  | 'Cancelled';

/**
 * AllTenants=1, ByProduct=2, ByPlan=3, SpecificTenants=4, Invited=5 (Platform scope);
 * AllMembers=6, ByModality=7, ByClass=8 (Tenant scope).
 */
export type LiveAudienceType =
  | 'AllTenants'
  | 'ByProduct'
  | 'ByPlan'
  | 'SpecificTenants'
  | 'Invited'
  | 'AllMembers'
  | 'ByModality'
  | 'ByClass';

/** Immediate=1, AfterReview=2, Unpublished=3. */
export type RecordingVisibilityType =
  | 'Immediate'
  | 'AfterReview'
  | 'Unpublished';

/** Live=1, Vod=2. */
export type WatchType = 'Live' | 'Vod';

/** Processing=1, Ready=2, Error=3. */
export type VideoAssetStatusType = 'Processing' | 'Ready' | 'Error';

/** Pending=1, Answered=2, Dismissed=3. */
export type LiveQuestionStatusType = 'Pending' | 'Answered' | 'Dismissed';

/** Like=1, Love=2, Clap=3, Wow=4, Laugh=5. */
export type ReactionType = 'Like' | 'Love' | 'Clap' | 'Wow' | 'Laugh';

/** WatchedMinutes=1, LiveCount=2, UniqueViewers=3, StorageSeconds=4. */
export type MetricType =
  | 'WatchedMinutes'
  | 'LiveCount'
  | 'UniqueViewers'
  | 'StorageSeconds';

/** Draft=1, Published=2 (Course and LearningPath). */
export type CourseStatusType = 'Draft' | 'Published';

/** Course=1, LearningPath=2. */
export type CertificateTargetType = 'Course' | 'LearningPath';

/** Student-view progress status for `/Courses/mine` and `/LearningPaths/mine`. */
export type LearningProgressStatus =
  | 'in_progress'
  | 'completed'
  | 'not_started';

/** Scope filter for `GET /LiveSubscriptions/mine`. */
export type MineSubscriptionScope = 'upcoming' | 'past' | 'all';

// ----------------------------------------------------------------------------
// Numeric → name lookup tables (SignalR enum coercion only — never REST)
// ----------------------------------------------------------------------------

export const LIVE_EVENT_STATUS_BY_NUMBER: Record<number, LiveEventStatusType> =
  {
    1: 'Scheduled',
    2: 'Live',
    3: 'Ended',
    4: 'Recorded',
    5: 'Cancelled',
  };

export const REACTION_TYPE_BY_NUMBER: Record<number, ReactionType> = {
  1: 'Like',
  2: 'Love',
  3: 'Clap',
  4: 'Wow',
  5: 'Laugh',
};

export const LIVE_QUESTION_STATUS_BY_NUMBER: Record<
  number,
  LiveQuestionStatusType
> = {
  1: 'Pending',
  2: 'Answered',
  3: 'Dismissed',
};

// ----------------------------------------------------------------------------
// Name → numeric wire codes for REST WRITE bodies, keyed by FIELD name.
// Codes come from the reference comments in `docs/academy-api.types.ts`.
// ----------------------------------------------------------------------------

export const ACADEMY_ENUM_WIRE_CODES: Readonly<
  Record<string, Readonly<Record<string, number>>>
> = {
  OwnerType: { Platform: 1, Tenant: 2 },
  ProductType: { FacilFit: 1, Actuar: 2, Ediz: 3, Toletus: 4, Tickou: 5 },
  LiveEventStatusType: {
    Scheduled: 1,
    Live: 2,
    Ended: 3,
    Recorded: 4,
    Cancelled: 5,
  },
  LiveAudienceType: {
    AllTenants: 1,
    ByProduct: 2,
    ByPlan: 3,
    SpecificTenants: 4,
    Invited: 5,
    AllMembers: 6,
    ByModality: 7,
    ByClass: 8,
  },
  RecordingVisibilityType: { Immediate: 1, AfterReview: 2, Unpublished: 3 },
  WatchType: { Live: 1, Vod: 2 },
  LiveQuestionStatusType: { Pending: 1, Answered: 2, Dismissed: 3 },
  ReactionType: { Like: 1, Love: 2, Clap: 3, Wow: 4, Laugh: 5 },
  MetricType: {
    WatchedMinutes: 1,
    LiveCount: 2,
    UniqueViewers: 3,
    StorageSeconds: 4,
  },
  CourseStatusType: { Draft: 1, Published: 2 },
  CertificateTargetType: { Course: 1, LearningPath: 2 },
};

/**
 * Array-valued enum fields → the scalar code table they reuse. Feature 010 replaced the
 * single `ProductType` scope with a `Products` LIST (multi-app content scope): empty =
 * platform-wide, one+ = only those apps see/unlock the content. The list rides the same
 * `ProductType` codes (FacilFit=1 … Tickou=5); the scalar tables above can't reach it
 * because their members are single strings, so the converters handle it separately.
 */
export const ACADEMY_ENUM_ARRAY_FIELDS: Readonly<Record<string, string>> = {
  Products: 'ProductType',
};

/** Numeric wire code → enum NAME, per field — inverse of `ACADEMY_ENUM_WIRE_CODES`. */
export const ACADEMY_ENUM_NAMES_BY_CODE: Readonly<
  Record<string, Readonly<Record<number, string>>>
> = Object.fromEntries(
  Object.entries(ACADEMY_ENUM_WIRE_CODES).map(([field, codes]) => [
    field,
    Object.fromEntries(
      Object.entries(codes).map(([name, code]) => [code, name])
    ),
  ])
);

/**
 * Returns a copy of a flat write body (or array of bodies) with every known enum
 * field translated from its NAME to the numeric wire code. Unknown fields and
 * already-numeric values pass through untouched.
 */
export function toWireEnums<T>(body: T): T {
  if (Array.isArray(body)) return body.map((item) => toWireEnums(item)) as T;
  if (body === null || typeof body !== 'object') return body;
  const out: Record<string, unknown> = { ...(body as Record<string, unknown>) };
  for (const [field, codes] of Object.entries(ACADEMY_ENUM_WIRE_CODES)) {
    const value = out[field];
    if (typeof value === 'string' && value in codes) out[field] = codes[value];
  }
  for (const [field, codeKey] of Object.entries(ACADEMY_ENUM_ARRAY_FIELDS)) {
    const value = out[field];
    if (!Array.isArray(value)) continue;
    const codes = ACADEMY_ENUM_WIRE_CODES[codeKey];
    out[field] = value.map((v) =>
      typeof v === 'string' && v in codes ? codes[v] : v
    );
  }
  return out as T;
}

/**
 * Inverse of `toWireEnums` for READ responses: returns a copy of a response
 * object (or array of objects) with every known enum FIELD translated from the
 * numeric wire code back to the NAME the TS unions declare. Name values pass
 * through untouched, so it stays safe if the backend gains a string converter.
 */
export function fromWireEnums<T>(body: T): T {
  if (Array.isArray(body)) return body.map((item) => fromWireEnums(item)) as T;
  if (body === null || typeof body !== 'object') return body;
  const out: Record<string, unknown> = { ...(body as Record<string, unknown>) };
  for (const [field, names] of Object.entries(ACADEMY_ENUM_NAMES_BY_CODE)) {
    const value = out[field];
    if (typeof value === 'number' && value in names) out[field] = names[value];
  }
  for (const [field, codeKey] of Object.entries(ACADEMY_ENUM_ARRAY_FIELDS)) {
    const value = out[field];
    if (!Array.isArray(value)) continue;
    const names = ACADEMY_ENUM_NAMES_BY_CODE[codeKey];
    out[field] = value.map((v) =>
      typeof v === 'number' && v in names ? names[v] : v
    );
  }
  return out as T;
}
